import _ from "lodash";
import _fp from "lodash/fp";
import { BackgroundTab, ClassroomStudentActivityState, WindowData } from "js/components/RegularClassroomUser/utils";

export type RawBackgroundTab =
	| {
			tabUrl?: string;
			url?: string;
			favIcon?: string;
			tab_id?: string;
			title?: string;
	  }
	| null
	| undefined;

export type RawWindowData = Omit<Partial<WindowData>, "background_tabs"> & { background_tabs?: RawBackgroundTab[] | null };

export interface RawScreenshotUser {
	user?: string;
	window_data?: RawWindowData[];
}

export interface TransformedScreenshotUser {
	username: string;
	screenshots: WindowData[];
}

function processBackgroundTabs(window: RawWindowData, tabs: RawBackgroundTab[] | null | undefined): BackgroundTab[] {
	if (tabs === null || tabs === undefined) {
		// debugging log (this can be removed eventually once we've found / eliminated the root cause)
		console.warn(`null background tabs for ${window.chrome_id} (platform: ${window.platform_type})`);
		return [];
	}

	// debugging log (this can be removed eventually once we've found / eliminated the root cause)
	const corruptedTabs = tabs.filter((tab) => !tab || !(tab.tabUrl || tab.url));
	if (corruptedTabs.length > 0) {
		console.warn(
			`Found ${corruptedTabs.length} corrupted background tabs for ${window.chrome_id} (platform: ${window.platform_type})`,
			corruptedTabs
		);
	}

	return tabs
		.filter((tab: RawBackgroundTab) => !!tab && !!(tab.tabUrl || tab.url))
		.map(
			(tab: RawBackgroundTab): BackgroundTab => ({
				favIcon: tab?.favIcon || "",
				tabUrl: tab?.tabUrl || tab?.url || "",
				tab_id: tab?.tab_id || "",
				title: tab?.title || "",
			})
		);
}

// TODO: Go through this to work out what transforms do and don't need. For anything we do need, try move it to the backend if feasible
export function transformScreenshotsResponse(data: RawScreenshotUser[]): TransformedScreenshotUser[] {
	const transformedData: TransformedScreenshotUser[] = [];

	for (const user of data) {
		if (!user.user || !user.window_data) {
			continue;
		}

		const mappedWindowData = user.window_data.map((windowData) => {
			windowData["chrome_id"] = windowData.chrome_id || "default";
			return windowData;
		});

		const screens: Record<string, RawWindowData[]> = _.flow(
			_fp.filter((x: RawWindowData) => !!x.chrome_id),
			_fp.sortBy("expiry"),
			_.reverse,
			_fp.uniqBy((val: RawWindowData) => `${val["chrome_id"]}:${val["chrome_window_id"]}`),
			_fp.groupBy("chrome_id")
		)(mappedWindowData);

		const transformedScreenshots: WindowData[] = [];

		for (const [, tiles] of Object.entries(screens)) {
			const mainScreen = tiles[0];

			const transformedWindow: WindowData = {
				chrome_id: mainScreen.chrome_id || "default",
				chrome_window_id: mainScreen.chrome_window_id || "",
				email: mainScreen.email || "",
				expiry: mainScreen.expiry || 0,
				favicon: mainScreen.favicon || "",
				platform_type: mainScreen.platform_type || "",
				screenshot: mainScreen.screenshot || "",
				tab_id: mainScreen.tab_id || "",
				teacherIds: mainScreen.teacherIds || [],
				title: mainScreen.title || "",
				url: mainScreen.url || "",

				// optional field
				restricted: mainScreen.restricted,
				state: mainScreen.state || ClassroomStudentActivityState.UNSET,
				status_message: mainScreen.status_message,
				capturedAt: mainScreen.capturedAt,
			};

			let backgroundTabs: BackgroundTab[] = processBackgroundTabs(mainScreen, mainScreen.background_tabs);

			// merge other tabs into the background tabs of the main screen
			for (let i = 1; i < tiles.length; i++) {
				const tile = tiles[i];
				const tabs = processBackgroundTabs(tile, tile.background_tabs);

				const hasTileTabInfo = !!tile.url || !!tile.tab_id || !!tile.title || !!tile.favicon;

				if (hasTileTabInfo) {
					backgroundTabs.push({
						favIcon: tile.favicon || "",
						tabUrl: tile.url || "",
						tab_id: tile.tab_id || "",
						title: tile.title || "",
					});
				}
				backgroundTabs = backgroundTabs.concat(tabs);
			}
			transformedWindow.background_tabs = backgroundTabs;
			transformedScreenshots.push(transformedWindow);
		}

		const transformedUser = {
			username: user.user,
			screenshots: _.sortBy(transformedScreenshots, ["chrome_id"]),
		};
		transformedData.push(transformedUser);
	}

	return transformedData;
}
